import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";
import { Trash2, Copy, Plus } from "lucide-react";

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [freshToken, setFreshToken] = useState(null); // { token, label } shown once
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tokens");
      setTokens(data.tokens || []);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    setError("");
    try {
      const { data } = await api.post("/admin/tokens", { label: label.trim() });
      setFreshToken(data); // plaintext returned exactly once
      setLabel("");
      load();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this token? The user will be signed out on their next action.")) return;
    try {
      await api.post(`/admin/tokens/${id}/revoke`);
      load();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  const copyToClipboard = (text) => {
    try {
      navigator.clipboard.writeText(text);
    } catch (e) { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="admin-page">
      <header className="bg-black text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/closer-helper-icon.png" alt="CH" className="h-8 w-8 rounded" />
          <div>
            <div className="font-display font-bold text-sm tracking-wide">Closer Helper — Admin</div>
            <div className="text-[11px] text-neutral-400 font-mono">{user?.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/")}
            className="text-xs px-3 py-1.5 rounded border border-neutral-700 hover:border-neutral-400 font-mono uppercase tracking-widest"
            data-testid="admin-open-app"
          >
            Open app
          </button>
          <button
            onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
            className="text-xs px-3 py-1.5 rounded bg-yellow-500 text-black hover:bg-yellow-400 font-mono font-semibold uppercase tracking-widest"
            data-testid="admin-logout"
          >
            Log out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-8">
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-display font-bold text-xl mb-4">Create beta access token</h2>
          <form onSubmit={create} className="flex gap-2" data-testid="create-token-form">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label (e.g. Alice — pilot #1)"
              className="flex-1 border border-slate-300 focus:border-slate-500 rounded-md px-3 py-2 text-sm outline-none"
              data-testid="input-token-label"
              required
            />
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-black hover:bg-neutral-800 disabled:opacity-60 text-white text-sm font-semibold"
              data-testid="submit-create-token"
            >
              <Plus className="h-4 w-4" />
              {creating ? "Creating…" : "Create"}
            </button>
          </form>
          {error && <p className="text-sm text-red-600 mt-3" data-testid="admin-error">{error}</p>}

          {freshToken && (
            <div className="mt-5 border-2 border-yellow-500 bg-yellow-50 rounded-xl p-4" data-testid="fresh-token-panel">
              <div className="text-[11px] uppercase tracking-widest text-yellow-800 font-mono mb-1">
                Copy now — shown only once
              </div>
              <div className="text-xs text-slate-700 mb-2">
                Label: <span className="font-semibold">{freshToken.label}</span>
              </div>
              <div className="flex gap-2">
                <code className="flex-1 font-mono text-xs bg-white border border-yellow-400 rounded px-3 py-2 break-all" data-testid="fresh-token-value">
                  {freshToken.token}
                </code>
                <button
                  onClick={() => copyToClipboard(freshToken.token)}
                  className="px-3 py-2 rounded bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold inline-flex items-center gap-1.5"
                  data-testid="copy-fresh-token"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </button>
                <button
                  onClick={() => setFreshToken(null)}
                  className="px-3 py-2 rounded border border-yellow-400 text-yellow-800 text-xs font-semibold"
                  data-testid="dismiss-fresh-token"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-display font-bold text-xl mb-4">Beta tokens</h2>
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : tokens.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No tokens yet. Create one above.</p>
          ) : (
            <table className="w-full text-sm" data-testid="tokens-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4">Label</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4">Last used</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100" data-testid={`token-row-${t.id}`}>
                    <td className="py-2.5 pr-4 font-medium">{t.label}</td>
                    <td className="py-2.5 pr-4">
                      {t.revoked ? (
                        <span className="inline-block rounded-full bg-red-100 text-red-700 text-[10px] font-semibold px-2 py-0.5 uppercase tracking-widest">
                          revoked
                        </span>
                      ) : (
                        <span className="inline-block rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold px-2 py-0.5 uppercase tracking-widest">
                          active
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-slate-500 text-xs">{fmt(t.created_at)}</td>
                    <td className="py-2.5 pr-4 text-slate-500 text-xs">{t.last_used_at ? fmt(t.last_used_at) : "—"}</td>
                    <td className="py-2.5 text-right">
                      {!t.revoked && (
                        <button
                          onClick={() => revoke(t.id)}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`revoke-${t.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
}

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
}
